import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Pagination.module.css';

export type PaginationProps = {
  page: number;
  perPage: number;
  total: number;
  onChange: (page: number) => void;
  /** Plural noun for the count line: "candidates", "staff". */
  label?: string;
};

/**
 * How many numbered buttons to show before collapsing the middle.
 *
 * A list of five hundred pages is a list of five hundred buttons otherwise, which is
 * unusable with a mouse and worse with a keyboard.
 */
const MAX_BUTTONS = 7;

export function pageNumbers(current: number, last: number): (number | 'gap')[] {
  if (last <= MAX_BUTTONS) {
    return Array.from({ length: last }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, last, current]);
  // One either side of where you are, so stepping through is always one click.
  if (current - 1 > 1) pages.add(current - 1);
  if (current + 1 < last) pages.add(current + 1);
  // Fill toward the ends so the row does not change width as you move.
  if (current <= 3) pages.add(2).add(3).add(4);
  if (current >= last - 2)
    pages
      .add(last - 1)
      .add(last - 2)
      .add(last - 3);

  const sorted = [...pages].filter((page) => page >= 1 && page <= last).sort((a, b) => a - b);

  const result: (number | 'gap')[] = [];
  let previous = 0;
  for (const page of sorted) {
    if (previous && page - previous > 1) result.push('gap');
    result.push(page);
    previous = page;
  }
  return result;
}

export function Pagination({
  page,
  perPage,
  total,
  onChange,
  label = 'items',
}: PaginationProps) {
  const last = Math.max(1, Math.ceil(total / perPage));
  if (total === 0) return null;

  const first = (page - 1) * perPage + 1;
  const shown = Math.min(page * perPage, total);

  return (
    <div className={styles.pager}>
      {/*
        A live region, because filtering and paging replace the rows silently.
        Sighted users see the table change; without this a screen-reader user gets
        no signal at all that a filter did anything, or that a search matched
        forty-three records instead of four hundred.

        Polite, so it waits for a gap rather than interrupting, and atomic so the
        whole sentence is read rather than just the digits that changed.
      */}
      <p className={styles.count} role="status" aria-live="polite" aria-atomic="true">
        Showing <b>{first}</b>–<b>{shown}</b> of <b>{total}</b> {label}
      </p>

      <nav className={styles.buttons} aria-label="Pagination">
        <button
          type="button"
          className={styles.button}
          disabled={page === 1}
          onClick={() => {
            onChange(page - 1);
          }}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} />
        </button>

        {pageNumbers(page, last).map((entry, index) =>
          entry === 'gap' ? (
            <span key={`gap-${index}`} className={styles.gap} aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={cx(styles.button, entry === page && styles.current)}
              onClick={() => {
                onChange(entry);
              }}
              aria-label={`Page ${entry}`}
              aria-current={entry === page ? 'page' : undefined}
            >
              {entry}
            </button>
          ),
        )}

        <button
          type="button"
          className={styles.button}
          disabled={page === last}
          onClick={() => {
            onChange(page + 1);
          }}
          aria-label="Next page"
        >
          <ChevronRight size={15} />
        </button>
      </nav>
    </div>
  );
}
